// Academic Session Utilities
// Academic year starts in April and ends in March of next year

export const getAcademicYear = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 1-12
  
  if (month >= 4) {
    return { startYear: year, endYear: year + 1 };
  } else {
    return { startYear: year - 1, endYear: year };
  }
};

export const getCurrentAcademicSession = () => {
  const { startYear, endYear } = getAcademicYear();
  return `${startYear}-${endYear}`;
};

export const getAcademicSessionMonths = (academicYear) => {
  const [startYear, endYear] = academicYear.split('-').map(Number);
  const months = [];
  
  // April to December of start year
  for (let month = 4; month <= 12; month++) {
    months.push({ year: startYear, month, name: getMonthName(month) });
  }
  
  // January to March of end year
  for (let month = 1; month <= 3; month++) {
    months.push({ year: endYear, month, name: getMonthName(month) });
  }
  
  return months;
};

export const isSummerVacation = (date) => {
  const month = date.getMonth() + 1; // 1-12
  const day = date.getDate();
  const year = date.getFullYear();
  
  // For 2025-26 session: Allow May 1-15 and June 26-31
  if (year === 2025) {
    // May: Only 16-31 is vacation (1-15 is working)
    if (month === 5 && day >= 16) return true;
    // June: Only 1-25 is vacation (26-31 is working)
    if (month === 6 && day <= 25) return true;
  } else {
    // Default behavior for other years: May 16 to June 30
    if (month === 5 && day >= 16) return true;
    if (month === 6) return true;
  }
  
  return false;
};

export const getDateType = (date, workingDays = [], events = []) => {
  // Use local date formatting to avoid timezone offset issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  const dayOfWeek = date.getDay();
  
  // Check for summer vacation first
  if (isSummerVacation(date)) {
    return 'summer-vacation';
  }
  
  // Check for events
  const event = events.find(e => e.date === dateStr);
  if (event && (event.type === 'holiday' || event.type === 'vacation')) return 'holiday';
  if (event && event.type === 'celebration') return 'celebration';
  
  // Check for Sunday (only non-working day in India)
  if (dayOfWeek === 0) return 'sunday';
  
  // All other days are working days (Monday-Saturday)
  return 'working';
};

export const getDateColor = (dateType) => {
  switch (dateType) {
    case 'summer-vacation': return '#ff6b35'; // Orange for summer vacation
    case 'holiday': return '#ef4444'; // Red for holidays
    case 'celebration': return '#fbbf24'; // Yellow for celebrations
    case 'sunday': return '#ef4444'; // Red for Sundays
    case 'working': return '#3b82f6'; // Blue for working days
    default: return '#3b82f6'; // Blue for working days (default)
  }
};

export const getDateLabel = (dateType) => {
  switch (dateType) {
    case 'summer-vacation': return 'Summer Vacation';
    case 'holiday': return 'Holiday';
    case 'celebration': return 'Celebration';
    case 'sunday': return 'Sunday';
    case 'working': return 'Working Day';
    default: return 'Working Day';
  }
};

export const getMonthName = (month) => {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return monthNames[month - 1];
};

export const getAcademicSessionSummary = (attendanceRecords = [], workingDays = []) => {
  const currentSession = getCurrentAcademicSession();
  const sessionMonths = getAcademicSessionMonths(currentSession);
  
  let totalPresent = 0;
  let totalAbsent = 0;
  let totalWorkingDays = 0;
  let totalSummerVacationDays = 0;
  
  sessionMonths.forEach(({ year, month }) => {
    // Count working days for this month
    const monthWorkingDays = workingDays.filter(dateStr => {
      const date = new Date(dateStr);
      return date.getFullYear() === year && date.getMonth() + 1 === month;
    });
    
    // Count summer vacation days
    const monthSummerDays = monthWorkingDays.filter(dateStr => {
      const date = new Date(dateStr);
      return isSummerVacation(date);
    }).length;
    
    totalWorkingDays += monthWorkingDays.length;
    totalSummerVacationDays += monthSummerDays;
    
    // Count attendance for this month
    const monthAttendance = attendanceRecords.filter(record => {
      const recordDate = new Date(record.date);
      return recordDate.getFullYear() === year && recordDate.getMonth() + 1 === month;
    });
    
    totalPresent += monthAttendance.filter(r => r.status === 'present').length;
    totalAbsent += monthAttendance.filter(r => r.status === 'absent').length;
  });
  
  const totalMarked = totalPresent + totalAbsent;
  const attendancePercentage = totalMarked > 0 ? ((totalPresent / totalMarked) * 100).toFixed(1) : 0;
  const overallPercentage = totalWorkingDays > 0 ? ((totalPresent / totalWorkingDays) * 100).toFixed(1) : 0;
  
  return {
    academicSession: currentSession,
    totalPresent,
    totalAbsent,
    totalMarked,
    totalWorkingDays,
    totalSummerVacationDays,
    attendancePercentage,
    overallPercentage,
    sessionMonths
  };
};